/**
 * Utility functions for workflow execution status badge colors.
 * Maps WorkflowExecutionStatus enum values to CloudScape badge colors.
 */

import { WorkflowExecutionStatus, WorkflowStepExecution, WorkflowStepExecutionStatus } from '@amzn/finspace-service-typescript-client';
import { BadgeColor } from '../constants/reportConstants';

/**
 * Checks if there are any failed or timed out steps in the workflow execution
 * @param stepExecutions - Array of workflow step executions to check
 * @returns true if any step has FAILED or TIMED_OUT status, false otherwise
 */
export const hasFailedOrTimedOutSteps = (stepExecutions?: WorkflowStepExecution[]): boolean => {
  return stepExecutions?.some((stepExec) => 
    stepExec.status === WorkflowStepExecutionStatus.FAILED ||
    stepExec.status === WorkflowStepExecutionStatus.TIMED_OUT
  ) || false;
};

/**
 * Gets the appropriate badge color for workflow execution status
 * @param status - The workflow execution status
 * @param stepExecutions - Optional array of step executions to check for failures
 */
export const getStatusBadgeColor = (
  status: WorkflowExecutionStatus | undefined,
  stepExecutions?: WorkflowStepExecution[]
): BadgeColor => {
  switch (status) {
    case WorkflowExecutionStatus.REPORT_GENERATION_SUCCEEDED:
      // If report succeeded but there were failed or timed out agent steps, use severity-low (yellow/warning)
      if (hasFailedOrTimedOutSteps(stepExecutions)) {
        return BadgeColor.SEVERITY_LOW;
      }
      return BadgeColor.GREEN;
    case WorkflowExecutionStatus.AGENT_EXECUTION_PENDING:
    case WorkflowExecutionStatus.AGENT_EXECUTION_IN_PROGRESS:
    case WorkflowExecutionStatus.REPORT_GENERATION_PENDING:
    case WorkflowExecutionStatus.REPORT_GENERATION_IN_PROGRESS:
    case WorkflowExecutionStatus.USER_INPUT_REQUIRED:
      return BadgeColor.BLUE;
    case WorkflowExecutionStatus.AGENT_EXECUTION_FAILED:
    case WorkflowExecutionStatus.REPORT_GENERATION_FAILED:
      return BadgeColor.RED;
    case WorkflowExecutionStatus.REPORT_GENERATION_SKIPPED:
    case WorkflowExecutionStatus.AGENT_EXECUTION_TIMED_OUT:
      return BadgeColor.SEVERITY_LOW;
    default:
      return BadgeColor.GREY;
  }
};
